package nl.volleyplanner.web

import dev.gitlive.firebase.FirebaseApp
import dev.gitlive.firebase.FirebaseOptions
import io.ktor.client.plugins.auth.providers.*
import io.ktor.http.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.*
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.format.DayOfWeekNames
import kotlinx.datetime.format.MonthNames
import kotlinx.datetime.format.char
import nl.volleyplanner.api.auth.AuthContextService
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.api.client.*
import nl.volleyplanner.lib.jsJodaTz
import nl.volleyplanner.nevoboservice.api.NevoboAPIKtorClient
import nl.volleyplanner.web.base.RootPageController
import nl.volleyplanner.web.base.WebPageController
import nl.volleyplanner.web.base.routing.PageRouter
import nl.volleyplanner.web.base.routing.RouteGuard
import nl.volleyplanner.web.lib.FirebaseAnalytics
import nl.volleyplanner.web.util.jsObject
import org.w3c.dom.HTMLElement
import kotlin.coroutines.CoroutineContext


val DAY_NAMES_LONG = DayOfWeekNames("maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag", "zondag")
val DAY_NAMES_SHORT = DayOfWeekNames("ma", "di", "wo", "do", "vr", "za", "zo")
val MONTH_NAMES_LONG = MonthNames("januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december")
val MONTH_NAMES_SHORT = MonthNames("jan", "feb", "mrt", "apr", "mei", "jun", "jul", "aug", "sep", "okt", "nov", "dec")
val DATE_TIME_FORMAT = LocalDateTime.Format { day(); char('-'); monthNumber(); char('-'); year(); char(' '); hour(); char(':'); minute() }
val DATE_TIME_SECOND_FORMAT = LocalDateTime.Format { day(); char('-'); monthNumber(); char('-'); year(); char(' '); hour(); char(':'); minute(); char(':'); second() }
val DATE_TIME_ISO_FORMAT = LocalDateTime.Format { day(); char('-'); monthNumber(); char('-'); year(); char('T'); hour(); char(':'); minute(); char(':'); second() }
val LOCAL_DT_TIME_FORMAT = LocalDateTime.Format { hour(); char(':'); minute() }
val LOCAL_TIME_TIME_FORMAT = LocalTime.Format { hour(); char(':'); minute() }
val DATE_FORMAT_ISO = LocalDate.Format { year(); char('-'); monthNumber(); char('-'); day() }
val DATE_FORMAT_ISO_BASIC = LocalDate.Format { year(); monthNumber(); day() }
val DATE_TEXT_FORMAT = LocalDate.Format { day(); char(' '); monthName(MONTH_NAMES_LONG); char(' '); year() }
val DATE_TEXT_LONG_FORMAT = LocalDate.Format { dayOfWeek(DAY_NAMES_LONG); char(' '); day(); char(' '); monthName(MONTH_NAMES_LONG); char(' '); year() }
val DATE_TEXT_NO_YEAR_SHORT_DAY_FORMAT = LocalDate.Format { dayOfWeek(DAY_NAMES_SHORT); char(' '); day(); char(' '); monthName(MONTH_NAMES_LONG) }
val DATE_TEXT_NO_YEAR_LONG_FORMAT = LocalDate.Format { dayOfWeek(DAY_NAMES_LONG); char(' '); day(); char(' '); monthName(MONTH_NAMES_LONG) }
val DATE_TEXT_NO_YEAR_SHORT_DAY_MONTH_FORMAT = LocalDate.Format { day(); char(' '); monthName(MONTH_NAMES_SHORT); }
val DATE_TEXT_SHORT_FORMAT = LocalDate.Format { day(); char(' '); monthName(MONTH_NAMES_SHORT); char(' '); year() }
val DATE_TIME_TEXT_SHORT_NAMES = LocalDateTime.Format { dayOfWeek(DAY_NAMES_SHORT); char(' '); day(); char(' '); monthName(MONTH_NAMES_SHORT); char(' '); hour(); char(':'); minute() }


fun setupLoggers() {
    if (AppEnvironment.current == AppEnvironment.Development) {
//        LogConfig.addLogger("MainApp", LogLevel.Info)
//        LogConfig.addLogger("nl.volleyplanner", LogLevel.Info)
//        LogConfig.addLogger("nl.volleyplanner.web", LogLevel.Info)
//        LogConfig.addLogger("nl.volleyplanner.web.base", LogLevel.Info)
        //LogConfig.addLogger("nl.volleyplanner.web.base.HTMLComponent", LogLevel.Debug)
//        LogConfig.addLogger("nl.volleyplanner.web.base.Controller", LogLevel.Info)
        //LogConfig.addLogger("nl.volleyplanner.web.util.StateUtil.SwitchingStateFlow", LogLevel.Debug)
        //LogConfig.addLogger("nl.volleyplanner.web.base.ComponentManager", LogLevel.Debug)
    }
}

enum class AppEnvironment {
    Development,
    Production;

    companion object {
        val current: AppEnvironment by lazy {
            when (BuildConfig.environment.lowercase()) {
                "prod", "production" -> Production
                else -> Development  // Default to Development for any other value
            }
        }
    }
}


interface WebApplication {
    val firebaseApp: FirebaseApp
    val firebaseAnalytics: FirebaseAnalytics
    val rootPages: List<RootPageController>

    fun init()
    fun getFirebaseOptions() : FirebaseOptions
    fun getRouteGuards(webPageController: WebPageController) : List<RouteGuard>
}

object VolleyplannerApiClientFactory {
    fun createVolleyplannerApiRootClient(tokenLoader: VolleyPlannerAuthTokenLoader?) =
        VolleyplannerRootApiClient.of(MainApp.apiDefaultRequestSpec, tokenLoader = tokenLoader)

    fun createVolleyplannerApiClient(organisationIdentifier: String, tokenLoader: VolleyPlannerAuthTokenLoader) =
        VolleyplannerApiClient.of(organisationIdentifier, MainApp.apiDefaultRequestSpec, tokenLoader = tokenLoader)

    fun createVolleyplannerApiPlannerClient(organisationIdentifier: String, tokenLoader: VolleyPlannerAuthTokenLoader) =
        VolleyplannerApiPlannerClient.of(organisationIdentifier, MainApp.plannerApiDefaultRequestSpec, tokenLoader = tokenLoader)

    fun createVolleyplannerApiFileUploadClient(organisationIdentifier: String, tokenLoader: VolleyPlannerAuthTokenLoader) =
        VolleyplannerApiFileUploadClient.of(organisationIdentifier, MainApp.plannerApiDefaultRequestSpec, tokenLoader = tokenLoader)
}

object VolleyPlannerWebAuthTokenLoader : VolleyPlannerAuthTokenLoader {
    override suspend fun loadTokens(): BearerTokens {
        require((AuthContextService.authUser?.authLevel ?: AuthLevel.AnonymousPublic) >= AuthLevel.BasicUserNotVerified)
        return BearerTokens(AuthContextService.apiTokenState.value!!, null)
    }

    override suspend fun refreshTokens(): BearerTokens {
        AuthContextService.refreshApiToken()
        return loadTokens()
    }
}

class VolleyPlannerAnonymousTokenLoader(val anonymousToken: String) : VolleyPlannerAuthTokenLoader {
    override suspend fun loadTokens(): BearerTokens {
        return BearerTokens(anonymousToken, null)
    }

    override suspend fun refreshTokens(): BearerTokens {
        return loadTokens()
    }
}

@OptIn(ExperimentalJsExport::class)
@JsExport
@JsName("Devtools")
object Devtools {
    fun updateLogger(prefix: String, level: String) = Logger.updateLoggers(prefix, LogLevel.valueOf(level))
}

// TODO: Migrate into (Abstract)WebApplication
//  Move planner/analyzer specific pieces to those projects
object MainApp : CoroutineScope {
    private val logger = Logger.getLogger("MainApp")

    private var _webApplication: WebApplication? = null
    val webApplication: WebApplication
        get() = _webApplication!!

    private var job = SupervisorJob()
    override val coroutineContext: CoroutineContext
        get() = Dispatchers.Main + job

    /**
     * The main content element from the web page
     */
    val contentElement = document.getElementById("main-content")!! as HTMLElement

    val nevoboApiClient = NevoboAPIKtorClient("https://europe-west1-volleyplanner.cloudfunctions.net/nevobo-api-proxy/", loggingEnabled = true)
    val apiDefaultRequestSpec = ApiDefaultRequestSpec(
        BuildConfig.apiHost, BuildConfig.apiPort, when (BuildConfig.apiProtocol) {
            "http" -> URLProtocol.HTTP
            "https" -> URLProtocol.HTTPS
            else -> throw IllegalArgumentException("Unknown apiProtocol: ${BuildConfig.apiProtocol}")
        }
    )
    val plannerApiDefaultRequestSpec = ApiDefaultRequestSpec(
        BuildConfig.apiPlannerHost, BuildConfig.apiPlannerPort, when (BuildConfig.apiProtocol) {
            "http" -> URLProtocol.HTTP
            "https" -> URLProtocol.HTTPS
            else -> throw IllegalArgumentException("Unknown apiProtocol: ${BuildConfig.apiProtocol}")
        }
    )
    val volleyanalyzerApiClient = VolleyanalyzerApiClient(apiDefaultRequestSpec)

    private fun addPageRoutes(basePath: String, pageInstance: WebPageController) {
        val path = basePath + pageInstance.path
        logger.debug("Registering route '$path' -> ${pageInstance.name}")
        // TODO: Better support for route guards through Page definitions etc. instead of taking info from controller
        //  also need to separate concerns from this (supposed to be) simple web application
        val guards = webApplication.getRouteGuards(pageInstance)
        PageRouter.register(path, guards) {
            pageInstance
        }

        logger.debug("Route registered")
        pageInstance.childPages.forEach { childPage ->
            addPageRoutes(path, childPage)
        }
    }

    fun init(webApplication: WebApplication) {
        setupLoggers()
        _webApplication = webApplication
        // We don't need any user tracking or cookies.
        // Ensure Google doesn't store anything
        nl.volleyplanner.web.lib.setConsent(
            jsObject {
                it.ad_personalization = "denied"
                it.ad_storage = "denied"
                it.ad_user_data = "denied"
                it.analytics_storage = "denied"
                it.functionality_storage = "denied"
                it.personalization_storage = "denied"
                it.security_storage = "denied"
            }
        )
        logger.info("Starting web app")
        logger.info("API Host: ${BuildConfig.apiHost}")
        logger.info("Planner API Host: ${BuildConfig.apiPlannerHost}")

        AuthContextService.launchService()

        jsJodaTz.toString()

        // Creating page routes
        webApplication.rootPages.forEach {
            rootPage -> addPageRoutes("", rootPage)
        }

        webApplication.init()

        window.onunload = { event ->
            CoroutineManager.APPLICATION_SCOPE.cancel("Unloading browser tab")
        }

        logger.debug("Launch routing")
        val routeScope = CoroutineScope(Dispatchers.Main + SupervisorJob() + CoroutineName("RoutingJob") + CoroutineManager.coroutineExceptionHandler)
        PageRouter.initialize(routeScope, contentElement)
        logger.debug("Routing launched")

        window.asDynamic().devtools = Devtools
    }
}
